import Box from '@mui/material/Box'
import { DateRangeCalendar } from '@mui/x-date-pickers-pro'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { isBefore, isAfter, isSameDay } from 'date-fns'
import React, { useState, useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'

import HHConfirmDialog from 'components/common/HHConfirmDialog'
import { PatchedAdapterDateFns } from 'providers/PatchedAdapterDateFns'
import {
  selectEventsByDate,
  selectExistingRentalFee,
  selectIsOngoingRentalFee,
  selectIsStartDateDeletable,
  selectReadOnly,
} from 'slices/pricing/rentalFeeDialogSlice'
import T from 'T'
import { formatDateToBEFormatDateFns } from 'utils/date'

import RentalFeeCalendarDay from './RentalFeeCalendarDay'
import RentalFeeDayPopover from './RentalFeeDayPopover'
import {
  buildBillingStatusByDate,
  buildRentalPeriodData,
  buildDayPriceByDate,
  findPeriodForDate,
  findPeriodForDatePreferStart,
  isClosedPeriod,
  canDateBeNewPeriodStart,
  wouldPeriodOverlap,
  wouldChangesAffectBilledDays,
  hasOrphanedBilledDaysInRange,
  getDraftInvoiceInfoForRange,
  getFinalizedInvoiceInfoForRange,
  getPeriodSpanEnd,
} from './rentalPeriodUtils'
import { calendarContainerStyles, getFlexibleCalendarStyles } from './styles'
import useRentalFeeActions from './useRentalFeeActions'

import type { DateRangePickerDayProps } from '@mui/x-date-pickers-pro/DateRangePickerDay'
import type { BilledPeriodApi, ConfiguredRentalFeeApi, RentalPeriodApi, RentalPeriodSpanApi } from 'types/api/rental-fee/rentalPeriod'

// RentalFeeCalendarDay and HHConfirmDialog are still untyped .jsx — remove these casts when they migrate.
const RentalFeeCalendarDayUntyped = RentalFeeCalendarDay as React.ElementType
const HHConfirmDialogUntyped = HHConfirmDialog as React.ElementType

type SingleMonthCalendarProps = {
  calendars: number
  slots: { day: (props: DateRangePickerDayProps, selectedDays?: unknown, pickersDayProps?: unknown) => React.ReactNode }
  sx?: unknown
  value: Date
  onChange: (value: Date) => void
}

// Used as a single-month calendar (calendars={1}); MUI's range-tuple value/onChange types don't model that.
const SingleMonthCalendar = DateRangeCalendar as unknown as React.ComponentType<SingleMonthCalendarProps>

// PatchedAdapterDateFns is still untyped .js — remove this cast when it migrates.
const PatchedAdapterDateFnsTyped = PatchedAdapterDateFns as unknown as React.ComponentProps<typeof LocalizationProvider>['dateAdapter']

type PeriodContext = 'closed' | 'ongoing' | 'outside'

type RentalFeeFormValues = {
  startDate: Date | null
  endDate: Date | null
  billingData: {
    unbilledPeriods: RentalPeriodSpanApi[]
    billedPeriods: BilledPeriodApi[]
    gracePeriods: RentalPeriodSpanApi[]
  } | null
  gracePeriodDays: number
  amount: number | null
  pricingPeriod: string
  activePeriodId: string | null
}

type RentalFeeCalendarProps = {
  displayRentalPeriods?: RentalPeriodApi[]
  flexibleHeight?: boolean
}

const RentalFeeCalendar = ({ displayRentalPeriods, flexibleHeight = false }: RentalFeeCalendarProps) => {
  const { watch } = useFormContext<{ rentalFee: RentalFeeFormValues }>()
  const rentalFee = watch('rentalFee')
  const { startDate, endDate, billingData, gracePeriodDays, amount: dayPrice, pricingPeriod, activePeriodId } = rentalFee

  const eventsByDate = useSelector(selectEventsByDate)
  const existingRentalFee = useSelector(selectExistingRentalFee) as ConfiguredRentalFeeApi | null
  const isOngoingRentalFee = useSelector(selectIsOngoingRentalFee) as boolean
  const isStartDateDeletable = useSelector(selectIsStartDateDeletable) as boolean
  const readOnly = useSelector(selectReadOnly) as boolean

  const {
    handleDaySelect,
    handleStartDateChange,
    handleEndDateChange,
    handleClearStartDate,
    handleClearEndDate,
    handleResetGracePeriod,
    handleUndoReset,
    handleEnterEditPeriod,
    handleDeleteRentalPeriod,
    isEndDateDeletable,
    canUndoReset,
    isSaveLoading,
  } = useRentalFeeActions()

  const [displayMonth, setDisplayMonth] = useState<Date>(startDate || new Date())
  const [popoverAnchorPosition, setPopoverAnchorPosition] = useState<{ top: number; left: number } | null>(null)
  const [popoverDate, setPopoverDate] = useState<Date | null>(null)
  const [popoverIsStartDate, setPopoverIsStartDate] = useState(false)
  const [popoverIsEndDate, setPopoverIsEndDate] = useState(false)
  const [popoverPeriodContext, setPopoverPeriodContext] = useState<PeriodContext | null>(null)
  const [popoverPeriod, setPopoverPeriod] = useState<RentalPeriodApi | null>(null)
  const [pendingSelectionDate, setPendingSelectionDate] = useState<Date | null>(null)
  const [periodToDelete, setPeriodToDelete] = useState<RentalPeriodApi | null>(null)

  const rentalPeriods = displayRentalPeriods

  const getPeriodContextForDate = useCallback(
    (day: Date): PeriodContext => {
      const dateStr = formatDateToBEFormatDateFns(day)
      const period = findPeriodForDate(dateStr, rentalPeriods)
      if (!period) return 'outside'
      return isClosedPeriod(period) ? 'closed' : 'ongoing'
    },
    [rentalPeriods]
  )

  const billingStatusByDate = useMemo(() => {
    if (!billingData) return {}
    const { unbilledPeriods = [], billedPeriods = [], gracePeriods = [] } = billingData
    const statusMap = buildBillingStatusByDate(unbilledPeriods, billedPeriods, gracePeriods)
    if (gracePeriodDays > 0 && rentalPeriods?.length) {
      rentalPeriods.forEach(({ startDate: periodStartDate }) => {
        if (periodStartDate) {
          statusMap[periodStartDate] = 'GRACE_PERIOD'
        }
      })
    }
    if (rentalPeriods?.length) {
      Object.keys(statusMap).forEach(dateStr => {
        if (statusMap[dateStr] === 'BILLED' && !findPeriodForDate(dateStr, rentalPeriods)) {
          delete statusMap[dateStr]
        }
      })
    }
    return statusMap
  }, [billingData, gracePeriodDays, rentalPeriods])

  const dayPriceByDate = useMemo(() => {
    if (!billingData || dayPrice == null) return {}
    const { billedPeriods = [], unbilledPeriods = [], gracePeriods = [] } = billingData
    return buildDayPriceByDate(billedPeriods, unbilledPeriods, dayPrice, pricingPeriod, rentalPeriods, gracePeriods)
  }, [billingData, dayPrice, pricingPeriod, rentalPeriods])

  const { rentalPeriodDates, rentalPeriodStartDates, rentalPeriodEndDates } = useMemo(
    () => buildRentalPeriodData(rentalPeriods, billingStatusByDate),
    [rentalPeriods, billingStatusByDate]
  )

  const calendarStyles = useMemo(() => (flexibleHeight ? getFlexibleCalendarStyles() : calendarContainerStyles), [flexibleHeight])

  const getAnchorPosition = useCallback((anchorElement: HTMLElement | null) => {
    if (!anchorElement || typeof anchorElement.getBoundingClientRect !== 'function') return null
    const rect = anchorElement.getBoundingClientRect()
    return { top: rect.bottom, left: rect.left + rect.width / 2 }
  }, [])

  const handlePopoverClose = useCallback(() => {
    setPopoverAnchorPosition(null)
    setPopoverDate(null)
    setPopoverIsStartDate(false)
    setPopoverIsEndDate(false)
    setPopoverPeriodContext(null)
    setPopoverPeriod(null)
    setPendingSelectionDate(null)
  }, [])

  const handlePopoverSetStartDate = useCallback(
    (newDate: Date) => {
      handleStartDateChange(newDate)
      if (endDate && isAfter(newDate, endDate)) {
        handleEndDateChange(null)
      }
    },
    [endDate, handleStartDateChange, handleEndDateChange]
  )

  const handlePopoverSetEndDate = useCallback(
    (newDate: Date) => {
      if (startDate && isBefore(newDate, startDate)) {
        handleEndDateChange(startDate)
        handleStartDateChange(newDate)
      } else {
        handleEndDateChange(newDate)
      }
    },
    [startDate, handleStartDateChange, handleEndDateChange]
  )

  const handlePopoverDeletePeriod = useCallback((period: RentalPeriodApi) => {
    setPeriodToDelete(period)
  }, [])

  const handleConfirmDeletePeriod = useCallback(() => {
    if (periodToDelete?.id) {
      handleDeleteRentalPeriod(periodToDelete.id)
    }
    setPeriodToDelete(null)
  }, [periodToDelete, handleDeleteRentalPeriod])

  const handleCancelDeletePeriod = useCallback(() => {
    setPeriodToDelete(null)
  }, [])

  const canSetStartDateForDay = useCallback(
    (day: Date): boolean => {
      if (activePeriodId) {
        if (!startDate) return false
        return !endDate || !isAfter(day, endDate)
      }
      return canDateBeNewPeriodStart(formatDateToBEFormatDateFns(day), rentalPeriods)
    },
    [activePeriodId, startDate, endDate, rentalPeriods]
  )

  const canSetEndDateForDay = useCallback(
    (day: Date): boolean => {
      if (!startDate) return false
      if (isBefore(day, startDate)) return false
      if (!activePeriodId && rentalPeriods?.length) {
        const startBE = formatDateToBEFormatDateFns(startDate)
        const dayBE = formatDateToBEFormatDateFns(day)
        if (wouldPeriodOverlap(startBE, dayBE, rentalPeriods)) return false
      }
      return true
    },
    [startDate, activePeriodId, rentalPeriods]
  )

  const handleDayClick = useCallback(
    (day: Date, anchorElement: HTMLElement | null, periodContext: PeriodContext) => {
      const dayStr = formatDateToBEFormatDateFns(day)
      const isClickedStartDate = Boolean((startDate && isSameDay(day, startDate)) || rentalPeriodStartDates?.has(dayStr))
      const isClickedEndDate = Boolean((endDate && isSameDay(day, endDate)) || rentalPeriodEndDates?.has(dayStr))

      handleDaySelect(day)

      if (readOnly) {
        setPendingSelectionDate(day)
        return
      }

      const periodForDay =
        (periodContext === 'closed' || periodContext === 'ongoing') && rentalPeriods?.length
          ? findPeriodForDatePreferStart(dayStr, rentalPeriods)
          : null

      const position = getAnchorPosition(anchorElement)
      setPopoverAnchorPosition(position)
      setPopoverDate(day)
      setPopoverIsStartDate(isClickedStartDate)
      setPopoverIsEndDate(isClickedEndDate)
      setPopoverPeriodContext(periodContext)
      setPopoverPeriod(periodForDay)
      setPendingSelectionDate(day)
    },
    [startDate, endDate, rentalPeriodStartDates, rentalPeriodEndDates, rentalPeriods, readOnly, handleDaySelect, getAnchorPosition]
  )

  const popoverCanSetStartDate = useMemo(
    () => (popoverDate ? canSetStartDateForDay(popoverDate) : false),
    [popoverDate, canSetStartDateForDay]
  )

  const popoverCanSetEndDate = useMemo(() => (popoverDate ? canSetEndDateForDay(popoverDate) : false), [popoverDate, canSetEndDateForDay])

  const popoverHasFinalizedBilledDays = useMemo(() => {
    if (!popoverPeriod?.startDate) return false
    return wouldChangesAffectBilledDays([popoverPeriod], [], existingRentalFee?.billedPeriods ?? [])
  }, [popoverPeriod, existingRentalFee?.billedPeriods])

  const popoverHasOrphanedBilledDays = useMemo(() => {
    if (!popoverPeriod?.startDate) return false
    const billedPeriods = existingRentalFee?.billedPeriods ?? []
    if (!billedPeriods.length) return false
    const periodEnd = getPeriodSpanEnd(popoverPeriod)
    return hasOrphanedBilledDaysInRange(popoverPeriod.startDate, periodEnd, billedPeriods)
  }, [popoverPeriod, existingRentalFee?.billedPeriods])

  const popoverCanDeletePeriod = useMemo(() => {
    if (!popoverPeriod?.id || !popoverPeriod.startDate) return false
    return !popoverHasFinalizedBilledDays
  }, [popoverPeriod?.id, popoverPeriod?.startDate, popoverHasFinalizedBilledDays])

  const popoverDraftInvoiceInfo = useMemo(() => {
    if (!popoverPeriod?.startDate) return null
    const billedPeriods = existingRentalFee?.billedPeriods ?? []
    if (!billedPeriods.length) return null
    const periodEnd = getPeriodSpanEnd(popoverPeriod)
    const info = getDraftInvoiceInfoForRange(popoverPeriod.startDate, periodEnd, billedPeriods)
    if (!info.invoiceIds.length) return null
    return info
  }, [popoverPeriod, existingRentalFee?.billedPeriods])

  const popoverFinalizedInvoiceInfo = useMemo(() => {
    if (!popoverPeriod?.startDate) return null
    const billedPeriods = existingRentalFee?.billedPeriods ?? []
    if (!billedPeriods.length) return null
    const periodEnd = getPeriodSpanEnd(popoverPeriod)
    const info = getFinalizedInvoiceInfoForRange(popoverPeriod.startDate, periodEnd, billedPeriods)
    if (!info.invoiceIds.length) return null
    return info
  }, [popoverPeriod, existingRentalFee?.billedPeriods])

  const handleMonthChange = useCallback((newMonth: Date) => {
    setDisplayMonth(newMonth)
  }, [])

  const renderDay = useCallback(
    (day: DateRangePickerDayProps, _selectedDays?: unknown, pickersDayProps?: unknown) => {
      if (day.outsideCurrentMonth) {
        return <Box sx={{ width: '100%', height: '100%' }} />
      }
      const dayObj: Date = day.day || day
      const periodContext = getPeriodContextForDate(dayObj)
      return (
        <RentalFeeCalendarDayUntyped
          billingStatusByDate={billingStatusByDate}
          day={day}
          dayPrice={dayPrice}
          dayPriceByDate={dayPriceByDate}
          endDate={endDate}
          eventsByDate={eventsByDate}
          gracePeriodDays={gracePeriodDays}
          isOngoingRentalFee={isOngoingRentalFee}
          pendingSelectionDate={pendingSelectionDate}
          periodContext={periodContext}
          pickersDayProps={pickersDayProps}
          pricingPeriod={pricingPeriod}
          rentalPeriodDates={rentalPeriodDates}
          rentalPeriodEndDates={rentalPeriodEndDates}
          rentalPeriods={rentalPeriods}
          rentalPeriodStartDates={rentalPeriodStartDates}
          startDate={startDate}
          onDayClick={handleDayClick}
        />
      )
    },
    [
      startDate,
      endDate,
      pendingSelectionDate,
      gracePeriodDays,
      rentalPeriods,
      rentalPeriodDates,
      rentalPeriodStartDates,
      rentalPeriodEndDates,
      billingStatusByDate,
      eventsByDate,
      dayPrice,
      dayPriceByDate,
      pricingPeriod,
      isOngoingRentalFee,
      getPeriodContextForDate,
      handleDayClick,
    ]
  )

  return (
    <Box>
      <LocalizationProvider dateAdapter={PatchedAdapterDateFnsTyped}>
        <SingleMonthCalendar
          calendars={1}
          slots={{ day: renderDay }}
          sx={calendarStyles}
          value={displayMonth}
          onChange={handleMonthChange}
        />
      </LocalizationProvider>
      <RentalFeeDayPopover
        activePeriodId={activePeriodId}
        anchorPosition={popoverAnchorPosition}
        canDeletePeriod={popoverCanDeletePeriod}
        canSetEndDate={popoverCanSetEndDate}
        canSetStartDate={popoverCanSetStartDate}
        canUndoReset={canUndoReset}
        date={popoverDate}
        draftInvoiceInfo={popoverDraftInvoiceInfo}
        finalizedInvoiceInfo={popoverFinalizedInvoiceInfo}
        hasOrphanedBilledDays={popoverHasOrphanedBilledDays}
        hasStartDate={Boolean(startDate)}
        isEndDate={popoverIsEndDate}
        isEndDateDeletable={isEndDateDeletable}
        isStartDate={popoverIsStartDate}
        isStartDateDeletable={isStartDateDeletable}
        open={Boolean(popoverAnchorPosition)}
        period={popoverPeriod}
        periodContext={popoverPeriodContext}
        onClearEndDate={handleClearEndDate}
        onClearStartDate={handleClearStartDate}
        onClose={handlePopoverClose}
        onDeleteRentalPeriod={handlePopoverDeletePeriod}
        onEnterEditPeriod={handleEnterEditPeriod}
        onResetGracePeriod={handleResetGracePeriod}
        onSetEndDate={handlePopoverSetEndDate}
        onSetStartDate={handlePopoverSetStartDate}
        onUndoReset={handleUndoReset}
      />
      <HHConfirmDialogUntyped
        ConfirmButtonProps={{
          loading: isSaveLoading,
          disabled: isSaveLoading,
          loadingPosition: 'center',
        }}
        confirmButtonTitle={T.DELETE}
        confirmDescription={T.DELETE_RENTAL_PERIOD_CONFIRM_MSG}
        confirmTitle={T.DELETE_RENTAL_PERIOD}
        DialogProps={{ maxWidth: 'xs', fullWidth: true }}
        isOpen={Boolean(periodToDelete)}
        onClose={handleCancelDeletePeriod}
        onConfirm={handleConfirmDeletePeriod}
      />
    </Box>
  )
}

export default RentalFeeCalendar
